// @ts-check
/**
 * The order's GST tax invoice: number, tax breakup, a download link and a WhatsApp button once
 * one exists; a "Generate now" button when it doesn't (e.g. billing settings or a product's tax
 * rate were missing when the order was paid, and have since been fixed).
 */
import { Download, Eye, FileText, MessageCircle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminOrderService from '../services/adminOrderService';
import { whatsappUrl } from '../utils/posOrder';
import Modal from './ui/Modal';

const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

/** @param {{order: any}} props */
const OrderInvoiceCard = ({ order }) => {
  const toast = useToast();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvoice(await adminOrderService.getInvoice(order.id));
    } catch {
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      setInvoice(await adminOrderService.generateInvoice(order.id));
      toast.success('Invoice generated');
    } catch (err) {
      toast.error(err.message || 'Could not generate the invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      setInvoice(await adminOrderService.regenerateInvoice(order.id));
      toast.success('Invoice PDF regenerated');
    } catch (err) {
      toast.error(err.message || 'Could not regenerate the invoice PDF');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return null;

  return (
    <div className="border rounded-lg border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={18} className="text-muted-foreground" />
        <h3 className="text-lg font-medium">Tax Invoice</h3>
      </div>

      {invoice ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium">
            {invoice.invoice_number}
            {order.order_status === 'cancelled' && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                Cancelled - report as cancelled to your CA
              </span>
            )}
          </p>
          <p className="text-muted-foreground">
            {invoice.is_intra_state
              ? `CGST ${inr(invoice.cgst)} + SGST ${inr(invoice.sgst)}`
              : `IGST ${inr(invoice.igst)}`}{' '}
            on {inr(invoice.taxable_value)}
            {invoice.discount_amount ? ` after a discount ${inr(invoice.discount_amount)}` : ''} · Total{' '}
            {inr(invoice.total)}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => setPreviewing(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50"
            >
              <Eye size={13} /> View
            </button>
            <a
              href={invoice.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50"
            >
              <Download size={13} /> Download PDF
            </a>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              title="Re-render the PDF from this order's current numbers"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
              {regenerating ? 'Regenerating...' : 'Regenerate PDF'}
            </button>
            {order.shipping_phone && (
              <a
                href={whatsappUrl(
                  order.shipping_phone_country_code || '+91',
                  order.shipping_phone,
                  `Hi ${order.shipping_first_name || 'there'}, here is your tax invoice for order ${order.order_number}: ${invoice.pdf_url}`,
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                <MessageCircle size={13} /> Send on WhatsApp
              </a>
            )}
          </div>

          {previewing && (
            <Modal maxWidth="4xl" onClose={() => setPreviewing(false)} hasUnsavedChanges={false}>
              <div className="h-[80vh]">
                <iframe
                  src={invoice.pdf_url}
                  title={`Tax invoice ${invoice.invoice_number}`}
                  className="w-full h-full border-0"
                />
              </div>
            </Modal>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-2">No invoice has been generated for this order yet.</p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate now'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderInvoiceCard;
